import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { ZodError } from 'zod';

import {
  DashboardFilterValues,
  CalendarWeekData,
  dashboardFilterCount,
} from '../api/types';
import CalendarFilterBar from '../components/CalendarFilterBar';
import CalendarViewToggle from '../components/CalendarViewToggle';
import DayColumn from '../components/DayColumn';
import DayShiftCard from '../components/DayShiftCard';
import DayStrip from '../components/DayStrip';
import RefreshFailedNote from '../components/RefreshFailedNote';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import ShowFilter from '../components/ShowFilter';
import SummaryPills from '../components/SummaryPills';
import WeekNav from '../components/WeekNav';
import WhyNotFillingBanner from '../components/WhyNotFillingBanner';
import useCalendarWeek from '../hooks/useCalendarWeek';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useCalendarNav from '../hooks/useCalendarNav';
import useIsPhone from '../hooks/useIsPhone';

import LoadingState from '@/components/LoadingState';

// Calendar tab: week nav + Today (left), summary pills + Show toggle (right),
// the approved horizontal filter bar beneath them (GRAVY-10305), and the
// 7-column day grid below.
//
// Filter state and the bar's revealed-ness are OWNED BY THE PAGE, not by this
// component (GRAVY-10321). They used to live here, and because the page mounts
// only the active tab, a glance at any action queue unmounted this component and
// silently discarded them. They arrive as props for the same reason the queues'
// filters do, and this tab feeds them straight into the calendar_week query key.

type Props = {
  agencyId: number;
  /** Owned by the page, so it survives tab switches (GRAVY-10321). */
  filters: DashboardFilterValues;
  onFiltersChange: (next: DashboardFilterValues) => void;
  /** Also owned by the page, for the same reason. */
  filtersOpen: boolean;
  onFiltersOpenChange: (next: boolean) => void;
};

// Week renders the seven-column grid; Day renders that single day's shifts as
// a list. Both are handed the SAME `days` payload — the server sends one entry
// for Day — so the two views cannot disagree about what is scheduled.
const DayOrWeek = ({
  view,
  days,
  onSelectShift,
  smartFill,
  filtered,
}: {
  view: 'day' | 'week';
  days: NonNullable<CalendarWeekData['days']>;
  onSelectShift: (id: number) => void;
  smartFill: boolean;
  /** Any filter or reason chip is on: an empty day may still have shifts. */
  filtered: boolean;
}) => {
  if (view === 'week') {
    return (
      <div className="dashv2-calendar">
        {days.map((day) => (
          <DayColumn
            key={day.date}
            day={day}
            onSelectShift={onSelectShift}
            smartFill={smartFill}
            filtered={filtered}
          />
        ))}
      </div>
    );
  }

  const shifts = days[0]?.shifts ?? [];
  if (shifts.length === 0) {
    // A day with nothing scheduled is a normal state, not a failure — say so
    // plainly rather than showing an empty frame the admin has to interpret.
    // With a filter on, the day may have shifts that just don't match (D7-02).
    return (
      <div className="dashv2-dayempty" data-testid="dayViewEmpty">
        <p>
          {filtered
            ? 'No shifts match these filters.'
            : 'No shifts scheduled for this day.'}
        </p>
      </div>
    );
  }

  return (
    <div className="dashv2-daylist" data-testid="dayViewList">
      {shifts.map((shift) => (
        <DayShiftCard
          key={shift.id}
          shift={shift}
          onOpen={onSelectShift}
          smartFill={smartFill}
        />
      ))}
    </div>
  );
};

const CalendarTab = ({
  agencyId,
  filters,
  onFiltersChange,
  filtersOpen,
  onFiltersOpenChange,
}: Props) => {
  const {
    view,
    setView,
    startDateISO,
    spanDays,
    rangeLabel,
    isOnToday,
    goPrev,
    goNext,
    goToday,
    goToDate,
    goPrevWeek,
    goNextWeek,
    startDate,
    weekDays,
    weekLabel,
  } = useCalendarNav();
  const isPhone = useIsPhone();

  // A phone shows one day at a time, as the client app does (GRAVY-10991): the
  // seven-column week grid has no room below 768px. A `?view=week` link opened
  // on a phone lands on the Day view of that week instead.
  useEffect(() => {
    if (isPhone && view === 'week') setView('day');
  }, [isPhone, view, setView]);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  // Where focus goes when the banner's last reason chip is cleared and the
  // banner unmounts (review N-08, edge).
  const calendarBodyRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useCalendarWeek(agencyId, startDateISO, filters, spanDays);
  const { data: filterOptions } = useDashboardFilters(agencyId);

  // Log the underlying cause so the failure is triageable rather than a silent
  // "Unable to load". A ZodError means the payload drifted from the contract;
  // anything else is an HTTP/request failure.
  useEffect(() => {
    if (!isError || !error) return;
    const kind = error instanceof ZodError ? 'contract' : 'request';
    // eslint-disable-next-line no-console
    console.error(`[DashboardV2] calendar_week ${kind} failure`, error);
    Sentry.captureException(error, {
      tags: { feature: 'dashboard_v2_calendar', failureKind: kind },
      extra: { agencyId, startDate: startDateISO },
    });
  }, [isError, error, agencyId, startDateISO]);

  const totals = data?.totals ?? { critical: 0, at_risk: 0, filled: 0 };
  const withReasons = !!data?.totals?.fill_reasons;

  // A band and a fill reason don't combine (Aly's prototype, 2026-10-14):
  // picking one clears the other, from the pills, the bar or the chips.
  const changeFilters = (next: DashboardFilterValues) => {
    const sameList = (a: string[] = [], b: string[] = []) =>
      a.length === b.length && a.every((v, i) => v === b[i]);
    if (
      next.fillStatus.length &&
      !sameList(next.fillStatus, filters.fillStatus)
    )
      onFiltersChange({ ...next, fillReason: [] });
    else if (
      next.fillReason?.length &&
      !sameList(next.fillReason, filters.fillReason)
    )
      onFiltersChange({ ...next, fillStatus: [] });
    else onFiltersChange(next);
  };

  return (
    <div>
      <div className="dashv2-controls">
        <div className="dashv2-navgroup">
          {/* On a phone the arrows step a week and the day strip picks the
              day (GRAVY-10991). */}
          <WeekNav
            rangeLabel={isPhone ? weekLabel : rangeLabel}
            unit={isPhone ? 'week' : view}
            onPrev={isPhone ? goPrevWeek : goPrev}
            onNext={isPhone ? goNextWeek : goNext}
          />
          {/* Today jumps; Week and Day are the two spans (GRAVY-10329). */}
          <CalendarViewToggle
            view={view}
            onViewChange={setView}
            onToday={goToday}
            isOnToday={isOnToday}
            todayOnly={isPhone}
          />
        </div>
        <div className="dashv2-summary">
          {/* The pills are the Calendar's fill-rate quick filter (GRAVY-10319).
              They read and write the SAME `fillStatus` the filter bar's Fill
              rate segment does — one canonical state, so the two cannot drift,
              Reset clears both, and a pill selection is part of the same
              calendar_week query key. */}
          <SummaryPills
            totals={totals}
            value={filters.fillStatus}
            onChange={(fillStatus) => changeFilters({ ...filters, fillStatus })}
          />
          <ShowFilter
            open={filtersOpen}
            onToggle={() => onFiltersOpenChange(!filtersOpen)}
            value={filters}
            withReasons={withReasons}
          />
        </div>
      </div>

      {filtersOpen && (
        <div id="dashv2-calendar-filters">
          <CalendarFilterBar
            options={filterOptions}
            value={filters}
            onChange={changeFilters}
            withReasons={withReasons}
          />
        </div>
      )}

      {/* Smart Fill (GRAVY-10888 round 2 V4): the reason counts, each a filter
          on the same calendar_week query. Renders nothing for a viewer who
          doesn't see Smart Fill (no `fill_reasons` in the totals). */}
      <WhyNotFillingBanner
        counts={data?.totals?.fill_reasons}
        value={filters.fillReason}
        onChange={(fillReason) => changeFilters({ ...filters, fillReason })}
        onEmptied={() => calendarBodyRef.current?.focus()}
      />

      {isPhone && (
        <DayStrip days={weekDays} selected={startDate} onSelect={goToDate} />
      )}

      <div
        ref={calendarBodyRef}
        tabIndex={-1}
        className="dashv2-calendarbody"
        data-testid="calendarBody"
        aria-label="Calendar"
      >
        {/* The error only with nothing to show: the calendar refreshes every
          30 seconds and React Query keeps the last week when a refresh fails,
          so one dropped refresh must not blank the cards under the banner's
          counts (review D4-06, as RT-07 for Shift Detail). */}
        {isLoading && !data ? (
          <LoadingState />
        ) : isError && !data ? (
          <div className="dashv2-stub">
            <p>Unable to load the calendar.</p>
            <button
              type="button"
              className="dashv2-retry"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        ) : (
          <>
            {isError && (
              <RefreshFailedNote
                onRetry={() => refetch()}
                retrying={isFetching}
              />
            )}
            <DayOrWeek
              view={view}
              days={data?.days ?? []}
              onSelectShift={setSelectedShiftId}
              smartFill={withReasons}
              filtered={dashboardFilterCount(filters, { withReasons }) > 0}
            />
          </>
        )}
      </div>

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}
    </div>
  );
};

export default CalendarTab;
